import { useCallback, useState } from "react";
import type { SkuCardData } from "@/components/chat/SkuCard";

/** Surfaces that can open ProductDetailModal → OrderConfirmSheet. */
export type ProductCheckoutCameFrom =
  | "chat"
  | "browse"
  | "vendor"
  | "landing"
  | "wishlist"
  | "dashboard"
  | "orders";

/** Same-vendor add-on selected in ProductDetailModal. */
export type CheckoutAddon = Pick<
  SkuCardData,
  "sku_id" | "name" | "price_aud" | "vendor_name" | "is_voucher"
>;

export interface UseProductCheckoutFlowOptions {
  cameFrom: ProductCheckoutCameFrom;
  /** Fired when the buyer taps View on a card (telemetry hook). */
  onViewCard?: (sku: SkuCardData) => void;
  /** Fired when the buyer commits from ProductDetailModal (telemetry hook). */
  onConfirmFromModal?: (sku: SkuCardData, addons: CheckoutAddon[]) => void;
}

/**
 * Shared View → ProductDetailModal → Send → OrderConfirmSheet state
 * machine. Used by InlineConcierge, DrawerBrowsePanel, and any surface
 * that renders SkuCard recommendation grids.
 */
export function useProductCheckoutFlow({
  cameFrom,
  onViewCard,
  onConfirmFromModal,
}: UseProductCheckoutFlowOptions) {
  const [viewingSku, setViewingSku] = useState<SkuCardData | null>(null);
  const [confirmingSku, setConfirmingSku] = useState<SkuCardData | null>(null);
  const [confirmingAddons, setConfirmingAddons] = useState<CheckoutAddon[]>(
    [],
  );

  const handleViewCard = useCallback(
    (sku: SkuCardData) => {
      onViewCard?.(sku);
      setViewingSku(sku);
    },
    [onViewCard],
  );

  const handleConfirmFromModal = useCallback(
    (sku: SkuCardData, addons: CheckoutAddon[] = []) => {
      onConfirmFromModal?.(sku, addons);
      setViewingSku(null);
      setConfirmingSku(sku);
      setConfirmingAddons(addons);
    },
    [onConfirmFromModal],
  );

  const handleCloseModal = useCallback(() => {
    setViewingSku(null);
  }, []);

  const handleCloseSheet = useCallback(() => {
    setConfirmingSku(null);
    setConfirmingAddons([]);
  }, []);

  /** Opens OrderConfirmSheet without the ProductDetailModal step (anchor shortcuts, quick-send CTAs). */
  const handleOpenConfirmSheet = useCallback(
    (sku: SkuCardData, addons: CheckoutAddon[] = []) => {
      setViewingSku(null);
      setConfirmingSku(sku);
      setConfirmingAddons(addons);
    },
    [],
  );

  return {
    cameFrom,
    viewingSku,
    confirmingSku,
    confirmingAddons,
    handleViewCard,
    handleConfirmFromModal,
    handleOpenConfirmSheet,
    handleCloseModal,
    handleCloseSheet,
    /** Clears modal, sheet, and add-on selections — useful on drawer close. */
    resetCheckout: useCallback(() => {
      setViewingSku(null);
      setConfirmingSku(null);
      setConfirmingAddons([]);
    }, []),
  };
}

export type ProductCheckoutFlow = ReturnType<typeof useProductCheckoutFlow>;
